/** */
import React from 'react';
import { Icon } from 'assets/icons';
import { GridRow, GridColumn } from 'app/member/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { SectionCard } from 'app/shared/section-card';
import { LineChart } from 'app/shared/charts/line';
import { StatCard } from 'app/shared/stat-card';
import { formatMonth } from 'utilities/date';
import { MetricLine } from './metric-line';
import { BreakdownRow } from './breakdown-row';
import styles from './acquisition-report.module.css';

const SIGNUP_LINES = [
  { key: 'signups', label: 'Signups', color: 'var(--green-primary)' },
  { key: 'activations', label: 'Activations', color: 'var(--blue-primary)' },
  { key: 'churned', label: 'Churned', color: 'var(--red-primary)' },
  { key: 'activation_rate', label: 'Activation Rate', color: 'var(--orange-primary)' }
];

export const AcquisitionReport = ({ leads = {}, people = {} }) => {
  const { metrics: lm = {}, tableData: lead_table = [] } = leads;
  const {
    metrics: pm = {},
    activationHistory = [],
    acquisitionBreakdown = {},
    acquisitionSources = []
  } = people;

  const stages = lead_table.filter((r) => r.stage);
  const max_stage = Math.max(1, ...stages.map((s) => s.count));
  const signup_data = activationHistory.map((p) => ({
    month: formatMonth(p.month),
    signups: p.signups,
    activations: p.activations,
    churned: p.churned,
    activation_rate: p.signups > 0 ? (p.activations / p.signups) * 100 : 0
  }));

  const top_industries = (acquisitionBreakdown.industry || []).slice(0, 5);
  const max_industry = Math.max(1, ...top_industries.map((r) => r.count));

  const referral_rows = acquisitionBreakdown.referral || [];
  const top_roles = (acquisitionBreakdown.qualification || []).slice(0, 4);

  return (
    <>
      <GridRow num_of_columns={5} auto_flow={false}>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="New Tenants" metric={pm.new_tenants} />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="Active Tenants" metric={pm.active_tenants} />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="Total Leads" metric={lm.total_leads} />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="Win Rate" metric={lm.win_rate} format="percent" />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="Activation Rate" metric={pm.activation_rate} format="percent" />
        </GridColumn>
      </GridRow>

      <Spacer multiple={4} />

      <GridRow num_of_columns={5} auto_flow={false}>
        <GridColumn span={3} auto_columns={false}>
          <SectionCard title="Signups & Activation — Last 13 Months">
            {signup_data.length === 0 ? (
              <div className={styles.empty}>No signup history available yet</div>
            ) : (
              <div className={styles.chartWrap}>
                <LineChart data={signup_data} lines={SIGNUP_LINES} xKey="month" rotateLabels />
              </div>
            )}
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="Acquisition Sources">
            {acquisitionSources.length === 0 ? (
              <div className={styles.empty}>No source data</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Signups</th>
                    <th>Activations</th>
                    <th>Activation Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {acquisitionSources.map((r) => (
                    <tr key={r.source}>
                      <td className={styles.sourceName} title={r.source}>
                        {r.source}
                      </td>
                      <td className={styles.amount}>{r.signups}</td>
                      <td className={styles.amount}>{r.activations}</td>
                      <td className={styles.amount}>{Number(r.activation_rate).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="Lead Pipeline Funnel">
            {stages.length === 0 ? (
              <div className={styles.empty}>No pipeline data</div>
            ) : (
              <div className={styles.funnel}>
                {stages.map((s, i) => (
                  <div key={s.stage}>
                    <div className={styles.funnelRow}>
                      <span className={styles.funnelLabel}>{s.label || s.stage}</span>
                      <div className={styles.funnelTrack}>
                        <div
                          className={styles.funnelFill}
                          style={{ width: `${(s.count / max_stage) * 100}%` }}
                        />
                      </div>
                      <span className={styles.funnelCount}>{s.count}</span>
                    </div>
                    {i < stages.length - 1 && (
                      <div className={styles.funnelConv}>
                        <span className={styles.convArrow}>
                          <Icon name="arrow_down" width={10} height={10} />
                        </span>
                        <span className={styles.convRate}>
                          {s.conversion_to_next > 0
                            ? `${Number(s.conversion_to_next).toFixed(1)}%`
                            : '—'}
                        </span>
                        <span className={styles.convLabel}>conversion to next</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </GridColumn>

        <GridColumn span={2} auto_columns={false}>
          <SectionCard title="Tenant Health">
            <div className={styles.metricLines}>
              <MetricLine label="Healthy" value={pm.healthy_tenants?.value} accent="accentGreen" />
              <MetricLine label="At risk" value={pm.at_risk_tenants?.value} accent="accentOrange" />
              <MetricLine label="Critical" value={pm.critical_tenants?.value} accent="accentRed" />
            </div>
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="Who's Signing Up">
            {top_industries.length > 0 && (
              <>
                <div className={styles.breakdownSection}>
                  <span className={styles.breakdownTitle}>Industry</span>
                  {top_industries.map((r) => (
                    <BreakdownRow key={r._id} label={r._id} count={r.count} max={max_industry} />
                  ))}
                </div>
                {(top_roles.length > 0 || referral_rows.length > 0) && (
                  <div className={styles.breakdownDivider} />
                )}
              </>
            )}
            {top_roles.length > 0 && (
              <div className={styles.breakdownSection}>
                <span className={styles.breakdownTitle}>Role</span>
                {top_roles.map((r) => (
                  <BreakdownRow
                    key={r._id}
                    label={r._id}
                    count={r.count}
                    max={Math.max(1, ...top_roles.map((x) => x.count))}
                  />
                ))}
              </div>
            )}
            {referral_rows.length > 0 && (
              <>
                {top_roles.length > 0 && <div className={styles.breakdownDivider} />}
                <div className={styles.breakdownSection}>
                  <span className={styles.breakdownTitle}>Channel</span>
                  {referral_rows.map((r) => (
                    <BreakdownRow
                      key={r._id}
                      label={r._id}
                      count={r.count}
                      max={Math.max(1, ...referral_rows.map((x) => x.count))}
                    />
                  ))}
                </div>
              </>
            )}
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="Speed to Lead">
            <div className={styles.metricLines}>
              <MetricLine
                label="Avg hrs to contact"
                value={lm.avg_hrs_to_contact?.value}
                format="hours"
              />
              <MetricLine
                label="Avg hrs to demo"
                value={lm.avg_hrs_to_demo?.value}
                format="hours"
              />
              <MetricLine
                label="Avg days to close"
                value={lm.avg_days_to_convert_from_demo?.value}
                format="days"
              />
            </div>
          </SectionCard>
        </GridColumn>
      </GridRow>
    </>
  );
};
